import axios from 'axios'
import {AuthModel, SmsChallengeResponse, UserModel} from './_models'

const API_URL = process.env.REACT_APP_API_URL

export const GET_USER_BY_ACCESSTOKEN_URL = `${API_URL}/Auth/get-user-by-token`
export const GET_TOKEN_URL = `${API_URL}/Auth/get-token`
export const GET_ACCESSTOKEN_WITH_TOKEN_URL = `${API_URL}/Auth/get-access-token-with-token`
export const LOGIN_URL = `${API_URL}/Auth/login`
export const REGISTER_URL = `${API_URL}/Auth/register`
export const REQUEST_PASSWORD_URL = `${API_URL}/Auth/forgot_password`
export const PROFILE_URL = `${API_URL}/Auth/profile`
export const PROFILE_PHOTO_URL = `${API_URL}/Auth/profile-photo`

export type UserProfileResponse = {
  firstName?: string
  lastName?: string
  roleLevel?: string
  positionName?: string
  image?: string
  imageUrl?: string
}

// Server should return AuthModel or SMS challenge information
export function login(
  userName: string,
  password: string,
  email?: string,
  smsCode?: string,
  deviceId?: string
) {
  const payload: Record<string, string> = {
    userName,
    password,
  }

  if (email !== undefined) {
    payload.userMail = email
  }

  if (smsCode) {
    payload.smsCode = smsCode
  }

  if (deviceId) {
    payload.deviceId = deviceId
  }

  return axios.post<AuthModel[] | SmsChallengeResponse>(GET_TOKEN_URL, payload)
}

// Server should return AuthModel
export function autologin(token: string, options?: {smsCode?: string; deviceId?: string}) {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    Accept: 'application/json',
  }

  if (options?.deviceId) {
    headers['X-Device-Id'] = options.deviceId
  }

  if (options?.smsCode) {
    headers['X-Sms-Code'] = options.smsCode
  }

  return axios.post<AuthModel[] | SmsChallengeResponse>(
    GET_ACCESSTOKEN_WITH_TOKEN_URL,
    {},
    {
      headers,
    }
  )
}

// Server should return AuthModel
export function refreshToken(refreshToken: string) {
  return axios.post<AuthModel[]>(
    GET_ACCESSTOKEN_WITH_TOKEN_URL,
    {},
    {
      headers: {
        Authorization: `Bearer ${refreshToken}`,
        Accept: 'application/json',
      },
    }
  )
}

// Server should return AuthModel
export function register(
  email: string,
  firstname: string,
  lastname: string,
  password: string,
  password_confirmation: string
) {
  return axios.put(REGISTER_URL, {
    email,
    first_name: firstname,
    last_name: lastname,
    password,
    password_confirmation,
  })
}

// Server should return object => { result: boolean } (Is Email in DB)
export function requestPassword(email: string) {
  return axios.post<{result: boolean}>(REQUEST_PASSWORD_URL, {
    email,
  })
}

export function getUserByToken(token: string) {
  return axios.post<UserModel>(
    GET_USER_BY_ACCESSTOKEN_URL,
    {
      api_token: token,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
}

export function getUserProfile() {
  return axios.get<UserProfileResponse>(PROFILE_URL)
}

export function updateUserProfile(profile: UserProfileResponse, token?: string) {
  return axios.post<UserProfileResponse>(
    PROFILE_URL,
    {
      firstName: profile.firstName,
      lastName: profile.lastName,
      positionName: profile.positionName,
    },
    {
      headers: {
        ...(token ? {Authorization: `Bearer ${token}`} : {}),
      },
    }
  )
}

export function getUserProfilePhoto() {
  return axios.get<{image: string; imageUrl?: string}>(PROFILE_PHOTO_URL)
}

export function uploadUserProfilePhoto(file: File, token?: string) {
  const formData = new FormData()
  formData.append('file', file)

  return axios.post<{image: string; imageUrl?: string}>(PROFILE_PHOTO_URL, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      ...(token ? {Authorization: `Bearer ${token}`} : {}),
    },
  })
}
